/**
 * usePageMeta — set document.title + OG / Twitter meta tags at runtime.
 *
 * Works for JS-executing crawlers (Google, Twitter, modern LinkedIn,
 * Slack Post Inspector). For no-JS crawlers, `index.html` carries
 * sensible defaults so shared links never fall back to blank previews.
 *
 * All updates are idempotent — existing tags are patched in place,
 * missing ones are created. Cleanup on unmount restores the previous
 * document.title only (leaves OG tags intact so back-navigation
 * previews don't flicker).
 */
import { useEffect } from "react";

function upsertMeta(attr, key, content) {
  if (typeof document === "undefined" || !content) return;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertLink(rel, href) {
  if (typeof document === "undefined" || !href) return;
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

export function usePageMeta({
  title,
  description,
  image,
  url,
  type = "article",
  siteName = "Nodera Health",
  twitterCard = "summary_large_image",
} = {}) {
  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    const prevTitle = document.title;

    if (title) document.title = title;

    // Open Graph
    upsertMeta("property", "og:title", title);
    upsertMeta("property", "og:description", description);
    upsertMeta("property", "og:image", image);
    upsertMeta("property", "og:url", url);
    upsertMeta("property", "og:type", type);
    upsertMeta("property", "og:site_name", siteName);

    // Twitter
    upsertMeta("name", "twitter:card", twitterCard);
    upsertMeta("name", "twitter:title", title);
    upsertMeta("name", "twitter:description", description);
    upsertMeta("name", "twitter:image", image);

    // Description fallback (non-OG)
    upsertMeta("name", "description", description);

    // Canonical
    upsertLink("canonical", url);

    return () => {
      document.title = prevTitle;
    };
  }, [title, description, image, url, type, siteName, twitterCard]);
}
